import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Plus, Sparkles, Loader2, Check, Copy, Trash2, Clock, Pencil } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageTip } from "@/components/FieldHelp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateTimePicker, utcToLocalInput } from "@/components/DateTimePicker";
import api from "@/lib/api";

const isLoom = (u) => { try { const h = new URL(u).hostname.toLowerCase(); return h === "loom.com" || h === "www.loom.com"; } catch { return false; } };
const NETWORKS = { linkedin_personal: "LinkedIn (personal note)", company_page: "Company page (later)" };
const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-emerald-500/15 text-emerald-500",
  due: "bg-amber-500/15 text-amber-500",
  posted: "bg-sky-500/15 text-sky-500",
};
const EMPTY = { network: "linkedin_personal", body: "", loom_url: "", link: "", scheduled_at: "", offer_id: "" };

export default function Social() {
  const [posts, setPosts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [tz, setTz] = useState("UTC");

  const load = async () => { const { data } = await api.get("/social"); setPosts(data); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/offers").then((r) => setOffers(r.data)).catch(() => {}); api.get("/org/status").then((r) => setTz(r.data.timezone || "UTC")).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ network: p.network, body: p.body || "", loom_url: p.loom_url || "", link: p.link || "",
      scheduled_at: p.scheduled_at ? utcToLocalInput(p.scheduled_at, tz) : "", offer_id: p.offer_id || "" });
    setEditId(p.id); setOpen(true);
  };

  const draftAi = async () => {
    if (!form.offer_id) return toast.error("Pick an offer to draft from");
    setDrafting(true);
    try { const { data } = await api.post("/social/draft", { offer_id: form.offer_id, network: form.network }); setForm((f) => ({ ...f, body: data.body })); toast.success("Draft written from your offer + proof"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Draft failed"); } finally { setDrafting(false); }
  };

  const save = async () => {
    if (form.loom_url && !isLoom(form.loom_url)) return toast.error("Loom link must be a loom.com share URL");
    if (!form.body.trim()) return toast.error("Write or draft the post body");
    try {
      const payload = { ...form, offer_id: form.offer_id || null };
      if (editId) await api.put(`/social/${editId}`, payload); else await api.post("/social", payload);
      toast.success(editId ? "Post updated (back to draft)" : "Draft saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not save"); }
  };

  const approve = async (p) => {
    if (!p.scheduled_at) return toast.error("Set a scheduled time before approving");
    try { await api.post(`/social/${p.id}/approve`); toast.success("Approved - will go due at its scheduled time"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not approve"); }
  };
  const markPosted = async (p) => { await api.post(`/social/${p.id}/posted`); toast.success("Marked posted"); load(); };
  const copyPost = (p) => {
    const parts = [p.body, p.loom_url, p.link].filter(Boolean);
    navigator.clipboard.writeText(parts.join("\n\n")); toast.success("Post copied");
  };
  const del = async (p) => { await api.delete(`/social/${p.id}`); toast("Deleted"); load(); };

  return (
    <PageBody>
      <PageHeader eyebrow="Social" title="Plan and approve your own posts"
        subtitle="Draft LinkedIn posts from your offer and proof, approve them, and schedule. DealsOps never posts to personal LinkedIn profiles - copy the post and publish by hand."
        actions={<Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={openNew} data-testid="new-social-btn"><Plus className="h-4 w-4 mr-1.5" /> New post</Button>} />

      <PageTip id="social">
        Approve is required before a post can go <span className="text-foreground font-medium">due</span>. Until a real company-page token exists, publishing is manual: use <span className="text-foreground font-medium">Copy post</span>, publish, then <span className="text-foreground font-medium">Mark posted</span>.
      </PageTip>

      {posts.length === 0 ? (
        <EmptyState icon={Share2} title="No posts yet" body="Draft your first LinkedIn post from an offer." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="social-list">
          {posts.map((p) => (
            <div key={p.id} className="rounded-xl border border-border/70 bg-card/50 p-5 flex flex-col" data-testid={`social-post-${p.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{NETWORKS[p.network]}</span>
                <Badge className={`border-0 text-[10px] capitalize ${STATUS_STYLE[p.status] || ""}`} data-testid={`social-status-${p.id}`}>{p.status}</Badge>
              </div>
              <p className="text-sm whitespace-pre-wrap flex-1">{p.body}</p>
              {p.loom_url && <a href={p.loom_url} target="_blank" rel="noreferrer" className="text-xs text-sky-500 hover:underline mt-2 break-all">{p.loom_url}</a>}
              {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="text-xs text-sky-500 hover:underline mt-1 break-all">{p.link}</a>}
              <div className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {p.scheduled_at_display ? p.scheduled_at_display : <span className="text-rose-500">No time scheduled</span>}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {p.status === "draft" && <Button size="sm" className="rounded-md bg-emerald-500 text-black hover:bg-emerald-400" onClick={() => approve(p)} data-testid={`approve-social-${p.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>}
                {(p.status === "approved" || p.status === "due") && <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={() => markPosted(p)} data-testid={`mark-posted-${p.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Mark posted</Button>}
                <Button size="sm" variant="outline" className="rounded-md" onClick={() => copyPost(p)} data-testid={`copy-post-${p.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy post</Button>
                {p.status !== "posted" && <Button size="sm" variant="ghost" className="rounded-md" onClick={() => openEdit(p)} data-testid={`edit-social-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                <Button size="sm" variant="ghost" className="rounded-md text-rose-500 ml-auto" onClick={() => del(p)} data-testid={`delete-social-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit post" : "New post"}</DialogTitle><DialogDescription>Draft from your offer + proof library. No invented metrics.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Network</Label>
              <Select value={form.network} onValueChange={(v) => setForm({ ...form, network: v })}>
                <SelectTrigger className="mt-1.5" data-testid="social-network"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(NETWORKS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Body</Label>
                <div className="flex items-center gap-2">
                  <Select value={form.offer_id} onValueChange={(v) => setForm({ ...form, offer_id: v })}>
                    <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="social-offer"><SelectValue placeholder="Offer for AI" /></SelectTrigger>
                    <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.product}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" className="text-amber-500 h-7" onClick={draftAi} disabled={drafting} data-testid="social-draft-ai">{drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}<span className="ml-1 text-xs">AI draft</span></Button>
                </div>
              </div>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="mt-1.5 text-sm" placeholder="Write your post, or draft it from an offer" data-testid="social-body" />
            </div>
            <div>
              <Label className="text-xs">Loom URL (optional)</Label>
              <Input value={form.loom_url} onChange={(e) => setForm({ ...form, loom_url: e.target.value })} placeholder="https://www.loom.com/share/…" className={`mt-1.5 ${form.loom_url && !isLoom(form.loom_url) ? "border-rose-500" : ""}`} data-testid="social-loom" />
            </div>
            <div>
              <Label className="text-xs">Offer-page link (optional)</Label>
              <Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" className="mt-1.5" data-testid="social-link" />
            </div>
            <div>
              <Label className="text-xs">Scheduled at (workspace time)</Label>
              <div className="mt-1.5">
                <DateTimePicker value={form.scheduled_at} tz={tz} onChange={(v) => setForm({ ...form, scheduled_at: v })} testId="social-scheduled-at" placeholder="Pick date & time" />
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="save-social-btn">{editId ? "Save changes" : "Save draft"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageBody>
  );
}
